import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api";

// Bulk staff import from an Excel/CSV sheet. All parsing, validation and
// saving happen on the backend (services/userImportService.js):
//   GET  /users/import/template  -> sample .xlsx with live reference data
//   POST /users/import/preview   -> per-row validation, nothing saved
//   POST /users/import           -> creates every valid row in one transaction

const errorMessage = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.error || fallback;

const StaffImportModal = ({ show, onClose, onImported }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const downloadSample = async () => {
    try {
      setBusy("template");
      const res = await api.get(`/users/import/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "staff-import-sample.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Template download failed", err);
      toast.error("Failed to download sample format");
    } finally {
      setBusy("");
    }
  };

  const buildForm = (f) => {
    const data = new FormData();
    data.append("file", f);
    return data;
  };

  const handleFile = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setPreview(null);
    setResult(null);

    try {
      setBusy("preview");
      const res = await api.post(`/users/import/preview`, buildForm(selected));
      setPreview(res.data);
    } catch (err) {
      console.error("Import preview failed", err);
      toast.error(errorMessage(err, "Could not read the file"));
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } finally {
      setBusy("");
    }
  };

  const runImport = async () => {
    if (!file) return;
    try {
      setBusy("import");
      const res = await api.post(`/users/import`, buildForm(file));
      setResult(res.data);
      setPreview(res.data);
      toast.success(res.data?.message || "Staff imported");
      onImported();
    } catch (err) {
      console.error("Import failed", err);
      const details = err.response?.data?.details;
      if (details?.rows) setPreview(details);
      toast.error(errorMessage(err, "Import failed"));
    } finally {
      setBusy("");
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal show fade d-block unit-modal"
      tabIndex="-1"
      style={{ background: "rgba(74, 14, 34, 0.55)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable admin-teal-modal admin-teal-modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center">
              <i className="fa-solid fa-file-import modal-header-icon" aria-hidden="true"></i>
              Import Staff from Excel
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              title="Close"
              aria-label="Close"
              disabled={!!busy}
            ></button>
          </div>

          <div className="modal-body">
            <div className="staff-import-steps">
              <div className="staff-import-step">
                <span className="staff-import-step-no">1</span>
                <div>
                  <strong>Download the sample format</strong>
                  <p>
                    The sample includes required columns, example rows, and reference
                    sheets with the current Roles, Units, Companies, Departments and Designations.
                  </p>
                  <button
                    type="button"
                    className="unit-btn unit-btn-light"
                    onClick={downloadSample}
                    disabled={!!busy}
                  >
                    <i className="fa-solid fa-download" aria-hidden="true"></i>
                    {busy === "template" ? "Preparing..." : "Download Sample (.xlsx)"}
                  </button>
                </div>
              </div>

              <div className="staff-import-step">
                <span className="staff-import-step-no">2</span>
                <div>
                  <strong>Upload the filled sheet</strong>
                  <p>Accepted: .xlsx, .xls, .csv (max 5 MB). Rows are validated before anything is saved.</p>
                  <label className="unit-btn unit-btn-light staff-import-file">
                    <i className="fa-solid fa-upload" aria-hidden="true"></i>
                    {busy === "preview" ? "Validating..." : file?.name || "Choose file"}
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFile}
                      disabled={!!busy}
                      hidden
                    />
                  </label>
                </div>
              </div>
            </div>

            {preview?.rows?.length > 0 && (
              <>
                <div className="staff-import-summary">
                  <span className="staff-import-pill">Total rows: {preview.total}</span>
                  <span className="staff-import-pill staff-import-pill-ok">Ready: {preview.valid}</span>
                  <span className="staff-import-pill staff-import-pill-err">With errors: {preview.invalid}</span>
                </div>

                {result && (
                  <div className={`alert ${preview.invalid ? "alert-warning" : "alert-success"} py-2`}>
                    Created <strong>{result.created}</strong> staff.
                    {preview.invalid > 0 && (
                      <> Skipped <strong>{preview.invalid}</strong> row(s) with errors — fix them and upload those rows again.</>
                    )}
                  </div>
                )}

                <div className="staff-import-table-wrap">
                  <table className="table table-sm staff-import-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Name</th>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Unit</th>
                        <th>Department</th>
                        <th>Designation</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r) => (
                        <tr key={r.rowNumber} className={r.errors.length ? "staff-import-row-err" : ""}>
                          <td>{r.rowNumber}</td>
                          <td>{r.values.name || "-"}</td>
                          <td>{r.values.username || "-"}</td>
                          <td>{r.values.email || "-"}</td>
                          <td>{r.values.role || "-"}</td>
                          <td>{r.values.unit || "-"}</td>
                          <td>{r.values.department || "-"}</td>
                          <td>{r.values.designation || "-"}</td>
                          <td>
                            {r.errors.length ? (
                              <span className="staff-import-errors">{r.errors.join("; ")}</span>
                            ) : (
                              <span className="staff-import-ok">{result ? "Created" : "Ready"}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button className="unit-btn unit-btn-light" onClick={handleClose} disabled={!!busy}>
              {result ? "Close" : "Cancel"}
            </button>
            {!result && (
              <button
                className="unit-btn unit-btn-primary"
                onClick={runImport}
                disabled={!!busy || !preview?.valid}
              >
                {busy === "import" ? "Importing..." : `Import ${preview?.valid || 0} Staff`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffImportModal;
